/*
IndexedDB layer — replaces Android Room for capture persistence.

Stores:
- captures:   user discoveries. Composite uniqueness on (arbreId, timestamp) for
              import dedup, mirrored from Android CaptureDao.captureExists(...).
- meta:       single-key bag (onboardingDone, splashIntroSeen, schemaVersion...).

The Paris tree dataset (~210k rows) is NOT mirrored into IDB. It stays as a
static GeoJSON asset under /data/, served by the SW with a cache-first strategy
and queried client-side by MapLibre. Trying to put 210k rows into IDB on iOS
would burn launch time and risk eviction.

For photos: we keep the raw Blob in IDB under store 'photos'. OPFS is the
better long-term home but IDB Blob support is broader and good enough at the
volumes we expect (personal use, hundreds not thousands of photos).
*/

const kDbName = 'canopaname'
const kDbVersion = 1
const kStoreCaptures = 'captures'
const kStoreMeta = 'meta'
const kStorePhotos = 'photos'
const kIndexCapturesByArbre = 'by_arbre_ts'

let fDb: IDBDatabase | null = null  // module-singleton handle, lazily initialized by openDb()

/*
Open (and migrate, if needed) the IndexedDB database. Idempotent — a second
call after success returns the existing handle.
*/
export function openDb(): Promise<IDBDatabase>
{
	if (fDb) return Promise.resolve(fDb)

	return new Promise((inResolve, inReject) => {
		const vReq = indexedDB.open(kDbName, kDbVersion)  // idb open request
		vReq.onupgradeneeded = (inEvent) => {
			const vDb = (inEvent.target as IDBOpenDBRequest).result  // upgrading db handle
			if (!vDb.objectStoreNames.contains(kStoreCaptures))
			{
				const vStore = vDb.createObjectStore(kStoreCaptures, { keyPath: 'id', autoIncrement: true })
				vStore.createIndex(kIndexCapturesByArbre, ['arbreId', 'timestamp'], { unique: true })
			}
			if (!vDb.objectStoreNames.contains(kStoreMeta))
			{
				vDb.createObjectStore(kStoreMeta, { keyPath: 'key' })
			}
			if (!vDb.objectStoreNames.contains(kStorePhotos))
			{
				vDb.createObjectStore(kStorePhotos, { keyPath: 'filename' })
			}
		}
		vReq.onsuccess = () => { fDb = vReq.result; inResolve(fDb) }
		vReq.onerror = () => inReject(vReq.error)
	})
}

/*
Run a single transaction with the given mode and stores. Wraps the IDB
transaction lifecycle into a Promise so call sites can use async/await.
*/
export async function withTx<T>(
	inStores: string[],
	inMode: IDBTransactionMode,
	inWork: (inTx: IDBTransaction) => Promise<T>,
): Promise<T>
{
	const vDb = await openDb()  // ensure open
	return new Promise<T>((inResolve, inReject) => {
		const vTx = vDb.transaction(inStores, inMode)  // transaction over the requested stores
		let vResult: T
		vTx.oncomplete = () => inResolve(vResult)
		vTx.onerror = () => inReject(vTx.error)
		vTx.onabort = () => inReject(vTx.error ?? new Error('Transaction aborted'))
		inWork(vTx).then((inVal) => { vResult = inVal }).catch(inReject)
	})
}

export const kStores = {
	captures: kStoreCaptures,
	meta: kStoreMeta,
	photos: kStorePhotos,
	indexCapturesByArbre: kIndexCapturesByArbre,
} as const
